import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setApiLocale } from '@/api/client';
import { DEFAULT_LOCALE, isLocaleCode, resolveLocale, type LocaleCode } from './locales';
import { en, type MessageKey, type Messages } from './messages/en';
import { es } from './messages/es';
import { pt } from './messages/pt';
import { de } from './messages/de';
import { fr } from './messages/fr';
import { it } from './messages/it';
import { ru } from './messages/ru';
import { tr } from './messages/tr';
import { zh } from './messages/zh';
import { ja } from './messages/ja';

const CATALOGS: Record<LocaleCode, Messages> = { en, es, pt, de, fr, it, ru, tr, zh, ja };

const STORAGE_KEY = 'richyplay-hub-lang';

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

interface I18nValue {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue | null>(null);

function readStoredLocale(): LocaleCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocaleCode(stored)) return stored;
  } catch {
    /* storage unavailable */
  }

  const preferred = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const tag of preferred) {
    const match = resolveLocale(tag);
    if (match) return match;
  }

  return DEFAULT_LOCALE;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // The API locale is set synchronously: child effects fetch before the provider effect runs.
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    const initial = readStoredLocale();
    setApiLocale(initial);
    return initial;
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => {
    setApiLocale(next);
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const t = useCallback<Translate>(
    (key, vars) => interpolate(CATALOGS[locale][key] ?? en[key] ?? key, vars),
    [locale],
  );

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Locales that keep the Latin product name must render it as English, otherwise
 * CSS uppercasing applies Turkish casing rules and "Client Hub" becomes "CLİENT HUB".
 */
export function brandLang(value: string): string | undefined {
  return /^[\x20-\x7E]+$/.test(value) ? 'en' : undefined;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}

export type { LocaleCode, MessageKey };
export { LOCALES } from './locales';
